/** Ось реакции кейса (`risk_case.reaction_status`). */
export const RISK_CASE_REACTION_STATUS_RU = {
    pending: 'Ещё не тронута',
    done: 'Обработана автоматикой',
    escalated: 'Требует рук',
    suppressed: 'Исторический бэклог — не тронута',
};
/** Все четыре значения, выведены из словаря — фильтр-валидатор (`filters-storage.ts`) не заводит вторую копию списка. */
export const RISK_CASE_REACTION_STATUSES = Object.keys(RISK_CASE_REACTION_STATUS_RU);
export const RISK_CASE_REACTION_STATUS_VARIANT = {
    pending: 'neutral',
    done: 'success',
    escalated: 'warning',
    suppressed: 'neutral',
};
/** Статус ПРОГОНА реакции (`risk_reaction_run.status`). */
export const RISK_REACTION_RUN_STATUS_RU = {
    running: 'Выполняется',
    completed: 'Завершён',
    failed: 'Ошибка',
    escalated: 'Эскалация',
    skipped: 'Пропущен',
};
export const RISK_REACTION_RUN_STATUS_VARIANT = {
    running: 'info',
    completed: 'success',
    failed: 'danger',
    escalated: 'warning',
    skipped: 'neutral',
};
/** Какой алерт запустил прогон: сам первый алерт кейса или повторный. */
export const RISK_REACTION_RUN_KIND_RU = {
    primary: 'Основной',
    duplicate: 'Повторный алерт',
};
/** Имя шага прогона (`risk_reaction_step.step`). */
export const RISK_REACTION_STEP_NAME_RU = {
    refund: 'Возврат',
    cancel_subscription: 'Отмена подписки',
    respond_alert: 'Ответ провайдеру',
};
/** Статус ШАГА. `shadow` — режим измерения: шаг записан, наружу ничего не ушло. */
export const RISK_REACTION_STEP_STATUS_RU = {
    ok: 'Выполнен',
    skipped: 'Пропущен',
    failed: 'Ошибка',
    shadow: 'Теневой режим',
};
export const RISK_REACTION_STEP_STATUS_VARIANT = {
    ok: 'success',
    skipped: 'neutral',
    failed: 'danger',
    shadow: 'info',
};
/**
 * Причина эскалации — строковый код, который пишет реактор
 * (`apps/worker/src/handlers/risk-reactor.ts`) в `RiskReactionRunDTO.error`
 * закрытого `escalated`-прогона.
 *
 * ТИПИЗИРОВАН `Record<RiskEscalationReason, string> & Record<string, string>`,
 * и пересечение делает две разные вещи сразу:
 *
 *  - ПЕРВАЯ половина превращает пропуск кода в ОШИБКУ КОМПИЛЯЦИИ. Список
 *    кодов раньше переписывался от руки трижды (здесь, в `REASON_RU` бота и в
 *    union'е самого реактора), и новый код доезжал до оператора сырой
 *    машинной строкой — молча, потому что фолбэк ниже намеренно всё прощает.
 *    Теперь единственный список — `RiskEscalationReason`
 *    (`@wit-support/risk-alerts`), и сборка падает, как только реактор узнал
 *    код, которого не знает этот словарь.
 *  - ВТОРАЯ половина оставляет ЛИШНИЕ ключи законными. `pending` ниже — ровно
 *    такой: это отдельная ось (`wait_identification` — прогон не закрывается
 *    вовсе), а не причина эскалации, но подпись ему нужна там же.
 *
 * Фолбэк остаётся сырым кодом, а не «неизвестной причиной»: машинный ключ
 * перед оператором честнее пустой строки.
 */
const RISK_ESCALATION_REASON_RU = {
    // ЛИШНИЙ КЛЮЧ (см. докблок): не эскалация, а «ждём идентификацию» —
    // `kase.identificationStatus` подан как есть.
    pending: 'Идентификация клиента ещё не завершена',
    ambiguous: 'Несколько кандидатов в клиенты — нужно подтверждение оператора',
    not_found: 'Клиент не найден в платёжном сервисе',
    unavailable: 'Идентификация недоступна',
    no_rule_matched: 'Ни одно включённое правило не совпало с кейсом',
    respond_failed: 'Источник отклонил отправку решения',
    respond_unknown_outcome: 'Исход отправки решения не подтверждён — нужна ручная сверка',
    respond_step_disabled: 'Совпавшее правило не отправляет решение провайдеру',
    // Обе — предусловия возврата (`executePrimary`, `risk-reactor.ts`). Матчер
    // отдаёт `matchedChargeId` с редакции контракта от 2026-08-27 — этот код
    // сегодня означает редкий дрейф (контракт назвал списание, а у нас его нет)
    // либо до-миграционный кейс, идентифицированный до этой редакции; второе —
    // `PAYMENT_API_BASE_URL` не задан.
    no_charge_id: 'Не найден ID списания для возврата',
    payment_not_configured: 'Платёжный сервис не настроен',
    refund_stuck_in_flight: 'Предыдущая попытка возврата зависла — повторите запуск вручную',
    refund_unknown_outcome: 'Исход возврата не подтверждён — нужна ручная сверка',
    refund_failed: 'Возврат отклонён платёжным сервисом',
    // Возврат СОЗДАН и ждёт действий клиента у провайдера — не отказ. Повтор
    // того же запроса позже безопасен (двухслойная идемпотентность контракта
    // возврата) и как раз и покажет итог, поэтому подпись зовёт повторить.
    refund_requires_action: 'Возврат создан, ждёт действий клиента у провайдера — повторите запуск позже',
    cancel_failed: 'Отмена подписки не удалась',
    // Дедлайн-свипер (`createRiskReactionSweeper`, `risk-reaction-sweeper.ts`) —
    // просроченный кейс форс-эскалируется независимо от флагов реакций.
    deadline_approaching: 'Истекает срок автоматического ответа',
};
export function riskEscalationReasonRu(reason) {
    if (reason === null)
        return null;
    return RISK_ESCALATION_REASON_RU[reason] ?? reason;
}
/**
 * Причина, по которой прогон закрылся `skipped`, — ОТДЕЛЬНАЯ ось от эскалации
 * и отдельный словарь.
 *
 * Коды сюда пишут два места `risk-reactor.ts`: ветка `skip`/`wait_identification`
 * при РУЧНОМ запуске (`finishRun('skipped', { error: reason })` — иначе нажатая
 * кнопка «Запустить реакцию» выглядела бы как не сработавшая) и
 * `already_actioned_elsewhere` из ветки дубликата. Смысл у них ровно обратный
 * эскалации: «делать было нечего», а не «сделать не удалось» — поэтому и
 * подписи утвердительные, и рисуются они нейтрально, а не тревожно.
 *
 * НЕ типизирован по union'у: в отличие от кодов эскалации, эти коды не
 * пересекают границу процесса (их не знает ни бот, ни контракты) и живут
 * ровно в одном файле реактора — сводить их в общий словарь домена значило бы
 * заводить вторую точку синхронизации там, где первой хватает.
 */
const RISK_REACTION_SKIP_REASON_RU = {
    already_actioned: 'По алерту уже отправлено решение',
    already_actioned_elsewhere: 'Решение по алерту отправил кто-то другой',
    duplicates_disabled: 'Авто-ответ на дубликаты выключен',
    reactions_disabled: 'Автоматические реакции выключены',
    wait_identification: 'Ждём идентификацию клиента',
    case_dismissed: 'Кейс отклонён оператором — автоматика его не трогает',
    case_escalated: 'Кейс уже в очереди «Требует рук»',
    case_suppressed: 'Кейс из исторического бэклога — автоматика его не трогает',
    not_actionable: 'Алерт не требует ответа (RDR/TC40/SAFE или терминальный код)',
    action_unsupported: 'Источник алерта не принимает решения',
};
/**
 * Подпись к `run.error` ЛЮБОГО прогона, а не только эскалированного.
 *
 * `skipped`-прогон тоже несёт код в `error`, и раньше UI его не рисовал вовсе:
 * оператор, нажавший «Запустить реакцию», видел прогон «Пропущен» без единого
 * слова о том, почему. Порядок разбора кода у `skipped` — сначала словарь
 * скипов, потом словарь эскалаций: shadow-прогон закрывается именно
 * `skipped`, но кладёт в `error` причину ЭСКАЛАЦИИ («что было бы сделано»),
 * и оба словаря нужны на одном статусе.
 *
 * `null` — рисовать нечего: либо кода нет, либо статус прогона к причине
 * отношения не имеет (`running`/`completed`/`failed` — у последнего в `error`
 * лежит текст ошибки, а не код, и он рисуется как есть).
 */
export function riskRunReasonRu(run) {
    if (run.error === null)
        return null;
    if (run.status === 'escalated')
        return riskEscalationReasonRu(run.error);
    if (run.status === 'skipped') {
        return RISK_REACTION_SKIP_REASON_RU[run.error] ?? riskEscalationReasonRu(run.error);
    }
    return null;
}
//# sourceMappingURL=reaction-labels.js.map